import React, { useEffect, useRef } from "react";
import {
  LABEL_PADDING,
  MAX_LABEL_FONT_SIZE,
  RIM_WIDTH,
  WHITE_RIM_WIDTH,
  WHEEL_SIZE,
  polarToCartesian,
} from "./wheelMath";
import {
  useRaffleWheelDynamic,
  useRaffleWheelStatic,
} from "./RaffleWheelContext";
import {
  CANVAS_OVERSAMPLE,
  WHEEL_CANVAS_COLORS,
  getSectorStrokeWidth,
  toRadians,
} from "./wheelCanvasUtils";
import cls from "./styles.module.scss";

export const WheelCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const {
    baseViewportScale,
    center,
    radius,
    scale,
    sectors,
    wheelSize,
  } = useRaffleWheelStatic();
  const { currentParticipantIndex } = useRaffleWheelDynamic();

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");

    if (!canvas || !context) return;

    const drawWheel = () => {
      const ratio = (window.devicePixelRatio || 1) * CANVAS_OVERSAMPLE;
      const logicalSize = WHEEL_SIZE * scale;
      const renderedSize = logicalSize * baseViewportScale;
      const renderScale = renderedSize / logicalSize;
      const { green, red, gold, orange, white } = WHEEL_CANVAS_COLORS;
      const sectorColors = [green, red, gold];

      canvas.width = renderedSize * ratio;
      canvas.height = renderedSize * ratio;
      context.setTransform(
        renderScale * ratio,
        0,
        0,
        renderScale * ratio,
        0,
        0
      );
      context.clearRect(0, 0, logicalSize, logicalSize);

      sectors.forEach((sector, index) => {
        context.beginPath();
        context.moveTo(center, center);
        context.arc(
          center,
          center,
          radius,
          toRadians(sector.startAngle),
          toRadians(sector.endAngle)
        );
        context.closePath();
        context.fillStyle =
          index === currentParticipantIndex
            ? orange
            : sectorColors[index % sectorColors.length];
        context.fill();
        context.strokeStyle = white;
        context.lineWidth = getSectorStrokeWidth(sector.sectorAngle);
        context.lineJoin = "round";
        context.stroke();

        if (sector.sectorAngle < 1) return;

        const labelRadius = radius - LABEL_PADDING;
        const labelPoint = polarToCartesian(
          sector.midAngle,
          labelRadius,
          center
        );
        const fontSize = Math.min(
          MAX_LABEL_FONT_SIZE * scale,
          sector.sectorAngle * 0.5 * scale
        );

        context.save();
        context.translate(labelPoint.x, labelPoint.y);
        context.rotate(toRadians(sector.midAngle));
        context.fillStyle = white;
        context.font = `600 ${fontSize}px Onest, Inter, sans-serif`;
        context.textAlign = "right";
        context.textBaseline = "middle";
        context.fillText(sector.participant.name, 0, 0);
        context.restore();
      });

      context.beginPath();
      context.arc(center, center, center - RIM_WIDTH / 2, 0, Math.PI * 2);
      context.strokeStyle = red;
      context.lineWidth = RIM_WIDTH;
      context.stroke();

      context.beginPath();
      context.arc(
        center,
        center,
        center - RIM_WIDTH - WHITE_RIM_WIDTH / 2,
        0,
        Math.PI * 2
      );
      context.strokeStyle = white;
      context.lineWidth = WHITE_RIM_WIDTH;
      context.stroke();
    };

    drawWheel();
    document.fonts?.ready.then(drawWheel);
  }, [
    baseViewportScale,
    center,
    currentParticipantIndex,
    radius,
    scale,
    sectors,
    wheelSize,
  ]);

  return (
    <canvas
      ref={canvasRef}
      className={cls.canvas}
      style={{
        width: `${wheelSize}px`,
        height: `${wheelSize}px`,
      }}
    />
  );
};
